import { Button } from "primereact/button"
import { BaseSyntheticEvent, MutableRefObject, RefObject, useEffect, useMemo, useRef, useState } from "react"
import { Combobox, EInputTypes, InputDate, InputTextCustom, Modal, useFormValidation, setNotification, ETypeOfNotification } from "medere-ui-framework"
import { AdministrativeFormItemDTO, ETaxType, EVatCustomerCategory, OperationType } from "../../interface/interface"
import { Checkbox } from "primereact/checkbox"
import { saveOrderOfProvision } from "../../servicesHooks/OrderOfProvision/useOrderOfProvisionService"
import Big from "big.js"
import OrderOfProvisionItems, { OrderOfProvisionItemsRef } from "./OrderOfProvisionItem"
import { EditOrder, ETaxes, ItemVaucher, MAX_DECIMAL, otherTaxes, otherTaxesToTotalAmountKeys, TaxType, TaxTypeDTO, TotalAmounts } from "../../interface/orderOfProvision"
import { OrderItemTaxes } from "./OrderOfProvisionTaxes"
import AccountingModal from "./AccountingModal"
import { creditNotes } from "../../enums/EAdministrativeFormType"
import useOrderOfProvision from "../../hooks/useOrderOfProvision"
import OrderOfProvisionTotals from "../../components/OrderOfProvisionTotals"
import { useDispatch } from "react-redux"
import TaxTypesComponent from "../Tax/TaxTypesComponents"
import TaxesPaidProvider, { PaidTax, useTaxesPaid } from "../../components/context/TaxesPaidContext"
import { findAAById } from "../../servicesHooks/Accounting/useAccountingService"
import { normalize } from '../../hooks/useOrderOfProvision'
import { useSupplierService } from "../../servicesHooks/Supplier/useSupplierService"


interface OrderItemTaxesRef {
    visibleTaxes: TaxType[],
    setTaxes: (newList: TaxType[]) => void,
    taxesWithTaxValues: TaxType[],
    taxes: TaxType[],
    taxesDTO: TaxTypeDTO[],
    getNetAmountNotTaxedItems: () => ItemVaucher[],
}

const useTotalAmounts = (items: ItemVaucher[], itemsTaxesRef: MutableRefObject<OrderItemTaxesRef> | RefObject<OrderItemTaxesRef>) => {
    const [totalAmounts, setTotalAmounts] = useState<TotalAmounts>({
        netTaxed: new Big(0),
        noTaxed: new Big(0),
        exempt: new Big(0),
        iva: new Big(0),
        percIva: new Big(0),
        iibb: new Big(0),
        others: new Big(0),
        total: new Big(0)
    })


    useEffect(() => {
        let _total = items.reduce((total, item) => total.plus(item.total), Big(0)).plus(totalAmounts.others).plus(totalAmounts.iibb).plus(totalAmounts.percIva)

        setTotalAmounts((prev) => ({ ...prev, total: _total }))
    }, [totalAmounts.netTaxed, totalAmounts.noTaxed, totalAmounts.iva, totalAmounts.exempt, totalAmounts.percIva, totalAmounts.iibb, totalAmounts.others])

    const getNetAmount = useMemo(() => {
        const _netAmount = items.filter((item) => {
            if (item.tax) return item.tax.taxtype != ETaxes.EXENTO && item.tax.taxtype != ETaxes.IVA_0
        })
        return _netAmount != null && _netAmount?.length > 0 ? _netAmount?.reduce((total: Big, item) => total.plus(item?.amount), Big(0)) : Big(0)
    }, [totalAmounts.total])

    const getNetAmountNotTaxed = useMemo(() => {
        const _netAmountNotTaxed = itemsTaxesRef.current?.getNetAmountNotTaxedItems()
        return _netAmountNotTaxed != null && _netAmountNotTaxed.length > 0 ? _netAmountNotTaxed.reduce((total: Big, item: ItemVaucher) => total.plus(item?.amount), Big(0)) : Big(0)
    }, [totalAmounts.total])

    // Este total es unicamente para los impuestos que no son IVA
    const totalOtherTaxes = useMemo(() => {
        return totalAmounts.iibb.plus(totalAmounts.others).plus(totalAmounts.percIva)
    }, [totalAmounts.iibb, totalAmounts.others, totalAmounts.percIva])

    function calculateTaxes() { //se basa en el contenido de la tabla
        let _netTaxed = Big(0)
        let _noTaxed = Big(0)
        let _exempt = Big(0)
        let _iva = Big(0)

        items.forEach(i => {
            if (i.tax) {
                _netTaxed = _netTaxed.plus(i.tax.taxvalue != ETaxType["IVA 0"] ? i?.amount : Big(0));
                _noTaxed = _noTaxed.plus(i.tax.taxvalue == ETaxType["IVA 0"] && !i.tax.exempttype ? i?.amount : Big(0))
                _exempt = _exempt.plus(i.tax.taxvalue == ETaxType["IVA EXENTO"] && i.tax.exempttype ? i?.amount : Big(0))
                _iva = _iva.plus(i.tax.taxvalue > ETaxType["IVA 0"] ? i?.amount.times(i.tax.taxvalue).div(100) : Big(0))
            }
        })
        setTotalAmounts((prev) => ({
            ...prev,
            netTaxed: new Big(normalize(_netTaxed, 2)),
            noTaxed: new Big(normalize(_noTaxed, 2)),
            exempt: new Big(normalize(_exempt, 2)),
            iva: new Big(normalize(_iva, 2))
        }))
    }
    return { totalAmounts, setTotalAmounts, calculateTaxes, getNetAmount, getNetAmountNotTaxed, totalOtherTaxes }
}


export default function OrderOfProvision({ selectedSupplier }: any) {

    const dispatch = useDispatch();
    const { generateAutomaticAccountingMovement, getAllAdministrativeFormTypes, getAllSuppliersCombo, getMovementsCombo } = useSupplierService();
    const itemTaxesRef = useRef<OrderItemTaxesRef>(null)
    const { orderInfo, setOrderInfo, noteCredit, setNoteCredit, accounting, items, setItems, defaultAccountingAccount } = useOrderOfProvision()
    const { totalAmounts, calculateTaxes, getNetAmount, getNetAmountNotTaxed, setTotalAmounts, totalOtherTaxes } = useTotalAmounts(items, itemTaxesRef)
    const [isLoading, setIsLoading] = useState(false)
    const itemsProvisionRef = useRef<OrderOfProvisionItemsRef>(null)
    const [accountingModal, setAccountingModal] = useState(false)
    const [modalTotalHaber, setModalTotalHaber] = useState(Big(0));

    const { handleValidation, errors, cleanError } = useFormValidation(orderInfo)
    const [infoModal, setInfoModal] = useState(false)
    const { paidTaxes, totalToPay, cleanAll, convertDuplicateItems, convertDuplicateTaxes } = useTaxesPaid();
    useEffect(() => {
        itemsProvisionRef.current?.refreshItems()
        calculateTaxes()
    }, [items])

    function addItemVaucherToArray(itemToVaucher: ItemVaucher) {
        const _id = crypto.randomUUID()

        setItems(prevItems =>
            [...prevItems,
            {
                ...itemToVaucher,
                description: itemToVaucher.description ?? '',
                id: _id,
                account: itemToVaucher.account ?? defaultAccountingAccount,
            }
            ]
        )

        const _taxes: TaxType[] | undefined = itemTaxesRef.current?.taxes
        if (_taxes && _taxes?.length > 0) {
            const _index: number = _taxes.findIndex((tax: TaxType) => {
                if (itemToVaucher.tax != null) return tax.taxType == itemToVaucher.tax.taxtype
            })
            if (_index != -1) {
                _taxes[_index].visible = true
                _taxes[_index].taxValue += Number(itemToVaucher.taxAmount)
                itemTaxesRef.current?.setTaxes(_taxes)
            }
        }
    }

    function deleteItemGrid(item: ItemVaucher) {
        const _taxes: TaxType[] | undefined = itemTaxesRef.current?.taxes
        const _tax = item.tax
        if (_tax && _taxes && _taxes?.length > 0) {
            const _index: number = _taxes.findIndex((tax: TaxType) => {
                return tax.taxType == _tax.taxtype
            })
            if (_index != -1) {
                _taxes[_index].taxValue -= Number(item.taxAmount)
                if (_taxes[_index].taxValue <= 0) {
                    _taxes[_index].taxValue = 0
                    _taxes[_index].visible = false
                }
                itemTaxesRef.current?.setTaxes(_taxes)
            }
        }
        setItems(items.filter(i => i.id != item.id))
    }


    function clean() {
        setTotalAmounts({
            netTaxed: new Big(0),
            noTaxed: new Big(0),
            exempt: new Big(0),
            iva: new Big(0),
            percIva: new Big(0),
            iibb: new Big(0),
            others: new Big(0),
            total: new Big(0)
        })
        setOrderInfo({
            supplier: null,
            pos: 0,
            voucherNumber: 0,
            reicipt: null,
            orderDate: new Date(),
            accountDate: undefined,
            expirationDate: undefined,
            payWithCreditCard: false,
            operationWithSGR: false,
            description: '',
        })
        setNoteCredit({
            noteCredit: null,
            administrativeFormItems: null,
            administrativeFormTaxes: null
        })

        cleanAll()

        setItems([])
    }

    function validateAccountingForItemsAndTaxes() {
        let error = false
        const _taxes: TaxTypeDTO[] | undefined = itemTaxesRef.current?.taxesDTO
        let _visibleTaxes = null
        if (_taxes && orderInfo.supplier.vatcategory != EVatCustomerCategory.MONOTRIBUTISTA) {
            _visibleTaxes = _taxes.filter((tax: TaxTypeDTO) => {
                if (tax.taxvalue > 0) {
                    return true
                }
                if (tax.taxtype == ETaxes.IVA_0 || tax.taxtype == ETaxes.EXENTO) {
                    return items.some((item: ItemVaucher) => {
                        if (item.tax) {
                            return item.tax.taxtype == tax.taxtype
                        }
                        return false
                    })
                }
                return false
            })
        }
        if (_visibleTaxes && _visibleTaxes.some((tax: TaxTypeDTO) => tax.taxaccount == null)) {
            error = true
        }
        return error
    }

    function groupBy<T, K extends keyof any>(array: T[], keyGetter: (item: T) => K): Record<K, T[]> {
        return array.reduce((result, item) => {
            const key = keyGetter(item);
            if (!result[key]) {
                result[key] = [];
            }
            result[key].push(item);
            return result;
        }, {} as Record<K, T[]>);
    }

    function saveOrder() {
        setIsLoading(true)
        let _taxes: TaxTypeDTO[] | undefined | null = itemTaxesRef.current?.taxesDTO
        if (orderInfo.supplier.vatcategory == EVatCustomerCategory.MONOTRIBUTISTA) {
            _taxes = null
        }
        const _orderDate = orderInfo.orderDate ?? orderInfo.orderDate ?? new Date()
        const _accountDate = orderInfo.accountDate ?? orderInfo.orderDate ?? new Date()
        const _expirationDate = orderInfo.expirationDate ?? orderInfo.orderDate ?? new Date()
        const taxValueMap: any = {
            [ETaxes.INGRESOS_BRUTOS]: Number(totalAmounts.iibb.toFixed(MAX_DECIMAL)),
            [ETaxes.PERCEPCIÓN_IVA]: Number(totalAmounts.percIva.toFixed(MAX_DECIMAL)),
            [ETaxes.OTROS_IMPUESTOS]: Number(totalAmounts.others.toFixed(MAX_DECIMAL))
        };
        const _taxesWithValues = paidTaxes.map(tax => ({
            taxtype: tax.taxtype,
            taxvalue: Big(tax?.amount),
            taxaccount: tax.taxAccount,
            taxname: tax.taxName
        }));

        return saveOrderOfProvision({ selectedSupplier: orderInfo.supplier, reicipt: orderInfo.reicipt, orderDate: _orderDate, accountDate: _accountDate, expirationDate: _expirationDate, items, pos: orderInfo.pos, voucherNumber: orderInfo.voucherNumber, netTaxed: totalAmounts.netTaxed, noTaxed: totalAmounts.noTaxed, exempt: totalAmounts.exempt, iva: totalAmounts.iva, percIva: totalAmounts.percIva, iibb: totalAmounts.iibb, others: totalAmounts.others, total: totalAmounts.total, payWithCreditCard: orderInfo.payWithCreditCard, operationWithSGR: orderInfo.operationWithSGR, taxes: _taxesWithValues, description: orderInfo.description, relatedAdministrativeForm: noteCredit.noteCredit?.administrativeform, paidTaxes: paidTaxes })
            .then(async (resp) => {
                await generateAutomaticAccountingMovement({ administrativeFormId: resp })
                dispatch(setNotification({
                    title: 'Carga de comprobante',
                    desc: 'El comprobante fue cargado exitosamente.',
                    typeOfNotification: ETypeOfNotification.INFO,
                    icon: 'pi pi-check'
                }))
                clean()
            })
            .catch(err => {
                dispatch(setNotification({
                    title: 'Carga de comprobante',
                    desc: 'No se pudo cargar el comprobante.',
                    typeOfNotification: ETypeOfNotification.ERROR,
                }))
            }).finally(() => {
                setIsLoading(false)
            })
    }

    async function calculateTotalModalHaber(data: { supplier: any; items: ItemVaucher[]; taxes: PaidTax[] | undefined; date: Date; formType: any; formNumber: string }) {
        let _taxes: PaidTax[] = [];
        let _items: ItemVaucher[] = [];
        _taxes = convertDuplicateTaxes(paidTaxes);
        _items = convertDuplicateItems(data.items);

        const _totalAmountTaxes = normalize(_taxes.reduce((acc, tax) => acc + Number(tax.amount), 0), 2);
        const _totalAmountItems = normalize(_items.reduce((acc, item) => acc.plus(item.amount), Big(0)), 2);

        const _total = Big(_totalAmountItems).plus(_totalAmountTaxes);

        const _accountingAccount = data.supplier?.debit;
        if (!_accountingAccount) return Big(0);

        const resp = await findAAById({
            accountingAccountId: _accountingAccount,
        });

        const _resp = {
            isSupplier: true,
            account: {
                ...resp
            },
            credit: normalize(Big(_total), 2),
        };

        const items: any = [..._taxes, ..._items, _resp];

        const calculatedTotalHaber = items.reduce((sum, item) => {
            if (item.taxValue) {
                return sum.plus(normalize(item.taxValue, 2));
            } else if (item.amount) {
                return sum.plus(normalize(item.amount, 2));
            }
            return sum;
        }, Big(0));

        return calculatedTotalHaber;
    }

    async function handleSaveAndUpdateOrder(e: BaseSyntheticEvent) {
        e.preventDefault()

        const total = await calculateTotalModalHaber({
            supplier: orderInfo.supplier ?? selectedSupplier,
            items: items,
            taxes: paidTaxes,
            date: orderInfo.orderDate ?? new Date(),
            formType: orderInfo.reicipt,
            formNumber: orderInfo.pos && orderInfo.voucherNumber ? `${orderInfo.pos} - ${orderInfo.voucherNumber}` : 'N Comprobante',
        })
        if (!total.eq(totalToPay)) {
            dispatch(setNotification({
                title: "No pudimos confirmar",
                desc: "Revisa el IVA",
                typeOfNotification: ETypeOfNotification.ERROR,
                icon: "pi pi-exclamation-triangle",
            }));
            return;
        }
        if (totalAmounts.total.eq(0)) {
            dispatch(setNotification({
                desc: 'El comprobante no puede tener un total de 0',
                typeOfNotification: ETypeOfNotification.ERROR,
            }))
            return
        }
        if (items.length == 0) {
            dispatch(setNotification({
                desc: 'Debe agregar al menos un ítem',
                typeOfNotification: ETypeOfNotification.ERROR,
            }))
            return
        }
        if (handleValidation(() => { }, errors, ['accountDate', 'expirationDate', 'operationWithSGR', 'payWithCreditCard', 'description']).invalidFields.length > 0) {
            return
        }

        if (validateAccountingForItemsAndTaxes()) {
            setInfoModal(true)
            return
        }

        if (creditNotes.includes(orderInfo?.reicipt?.administrativeformtype ?? -1)) {
            const taxValueMap: any = {
                [ETaxes.INGRESOS_BRUTOS]: totalAmounts.iibb,
                [ETaxes.PERCEPCIÓN_IVA]: totalAmounts.percIva,
                [ETaxes.OTROS_IMPUESTOS]: totalAmounts.others
            }
            if (noteCredit.noteCredit == null) {
                dispatch(setNotification({
                    desc: 'Debe seleccionar un comprobante para vincular',
                    typeOfNotification: ETypeOfNotification.ERROR,
                }))
                return
            }
            if (noteCredit.administrativeFormItems == null) return
            const _totalCredits = noteCredit.administrativeFormItems?.reduce((total: Big, item: AdministrativeFormItemDTO) => total.plus(Big(item.creditamount ?? 0)), Big(0))
            const toPay = Big(noteCredit.noteCredit.total).minus(_totalCredits)
            if (totalAmounts.total.cmp(toPay) > 0) {
                dispatch(setNotification({
                    desc: 'El total de la nota de crédito no puede ser mayor al total del comprobante a vincular',
                    typeOfNotification: ETypeOfNotification.ERROR,
                }))
                return
            }
            const groupByItems = groupBy(items, (item) => item?.account?.accountingaccount ?? -1)
            const hasAmountItemExceed = noteCredit.administrativeFormItems?.some(item => {
                const _totalItem = groupByItems[item.accountingaccount]?.reduce((total: Big, item: ItemVaucher) => total.plus(item.total), Big(0))
                // el total de los items agregados no debe superar el total del item del comprobante a vincular
                // sino hasAmountItemExceed = true
                const resultInvoiceItem = Big(item.totalamount).minus(item.creditamount ?? 0)
                return (_totalItem?.cmp(resultInvoiceItem) ?? 0) > 0
            })
            if (hasAmountItemExceed) {
                dispatch(setNotification({
                    desc: 'El monto de cada ítem en una nota de crédito no puede exceder el monto del ítem correspondiente en la factura original',
                    typeOfNotification: ETypeOfNotification.ERROR,
                }))
                return
            }

            if ((noteCredit?.administrativeFormTaxes?.length ?? 0) > 0) {
                let _taxes: TaxTypeDTO[] | undefined | null = itemTaxesRef.current?.taxesDTO
                const _otherTaxes = _taxes?.map(tax => ({
                    taxtype: tax.taxtype,
                    taxvalue: taxValueMap[tax.taxtype] || tax.taxvalue,
                    taxaccount: tax.taxaccount,
                    taxname: tax.taxname
                })).filter(tax => otherTaxes.includes(tax.taxtype)).filter((tax) => Big(tax.taxvalue).cmp(0) > 0)
                if (!_otherTaxes) return

                const isTaxInOriginalInvoice = _otherTaxes.some(tax => {
                    const _tax = noteCredit?.administrativeFormTaxes?.find(invoiceTax => invoiceTax.taxtype == tax.taxtype)
                    if (_tax) {
                        return false
                    }
                    return true
                })
                if (isTaxInOriginalInvoice) {
                    dispatch(setNotification({
                        desc: 'No se puede asignar un monto a un impuesto en una nota de crédito si dicho impuesto no está presente en la factura original',
                        typeOfNotification: ETypeOfNotification.ERROR,
                    }))
                    return
                }

            }

            const hasAmountTaxExceed = noteCredit?.administrativeFormTaxes?.some(invoiceItemTax => {
                const currentTaxValue = taxValueMap[invoiceItemTax.taxtype]
                // Si un impuesto editable (PERC.IVA, IIBB, OTROS) de la nota de crédito supera el monto del impuesto en factura original
                return currentTaxValue.cmp(invoiceItemTax.taxamount) > 0
            })
            if (hasAmountTaxExceed) {
                dispatch(setNotification({
                    desc: 'El monto de los impuestos (PERC.IVA, PERC.IBB, Otros impuestos) en una nota de crédito no puede exceder el monto del impuesto en la factura original',
                    typeOfNotification: ETypeOfNotification.ERROR,
                }))
                return
            }

        }
        saveOrder()
    }

    function convertOtherTaxesIntoTaxes(): TaxType[] {
        const _taxes = itemTaxesRef.current?.visibleTaxes
        if (!_taxes) return []
        return _taxes.filter((tax: TaxType) => otherTaxes.includes(tax.taxType)).map((tax) => {
            const totalAmountKey = otherTaxesToTotalAmountKeys[tax.taxType as ETaxes] as keyof TotalAmounts
            if (!totalAmountKey) return tax
            const totalAmountValue = totalAmounts[totalAmountKey]
            return {
                editable: tax.editable,
                taxAccount: tax.taxAccount,
                taxAccountDesc: tax.taxAccountDesc,
                taxAccountIdentifier: tax.taxAccountIdentifier,
                taxName: tax.taxName,
                taxType: tax.taxType,
                taxValue: Number(totalAmountValue?.toFixed(MAX_DECIMAL)),
                visible: tax.visible,
                exemptVatTax: tax.exemptVatTax,
                vatNotTaxed: tax.vatNotTaxed
            }
        })
    }

    return (
        <div className="mx-3 mt-4 h-screen flex flex-column gap-3">
            <h1 className="title text-3xl font-semibold">Proveedores</h1>
            <h2 className="title text-base font-semibold">Nuevo comprobante</h2>
            <div className="flex gap-3 md:max-w-42rem flex-column md:flex-row">
                <Combobox
                    getItems={getAllSuppliersCombo}
                    label="Proveedor"
                    value={orderInfo.supplier}
                    optionLabel="businessname"
                    setValue={(value) => setOrderInfo(prev => ({ ...prev, supplier: value }))}
                />
                <InputTextCustom
                    onChange={() => { }}
                    label="CUIT"
                    value={orderInfo?.supplier?.vatid ?? ''}
                    disable
                />
                <InputTextCustom
                    onChange={() => { }}
                    label="Condición"
                    value={orderInfo?.supplier?.vatcategorydesc ?? ''}
                    disable
                />
            </div>
            <form onSubmit={handleSaveAndUpdateOrder}>
                <div className="flex align-items-start gap-4 flex-column md:flex-row flex-wrap sm:flex-nowrap">
                    <Combobox
                        label="Comprobante"
                        getItems={getAllAdministrativeFormTypes}
                        value={orderInfo.reicipt}
                        setValue={(value) => {
                            if (errors.reicipt) {
                                cleanError('reicipt')
                            }
                            setOrderInfo({ ...orderInfo, reicipt: value })
                        }}
                        optionLabel="shortdescription"
                        error={errors.reicipt}
                        caption={errors.reicipt ? 'Debe seleccionar un tipo de comprobante' : ''}
                    />
                    <InputTextCustom
                        label='Punto venta'
                        inputType={EInputTypes.NUMBER}
                        value={orderInfo.pos}
                        onChange={(e: BaseSyntheticEvent) => {
                            if (errors.pos && e.target.value != '') {
                                cleanError('pos')
                            }
                            setOrderInfo({ ...orderInfo, pos: e.target.value })
                        }}
                        error={errors.pos}
                        caption={errors.pos ? 'Debe ingresar un punto de venta' : ''}
                        required
                    />
                    <InputTextCustom
                        label='Nº Compr.'
                        inputType={EInputTypes.NUMBER}
                        value={orderInfo.voucherNumber}
                        onChange={(e: BaseSyntheticEvent) => {
                            if (errors.voucherNumber && e.target.value != '') {
                                cleanError('voucherNumber')
                            }
                            setOrderInfo({ ...orderInfo, voucherNumber: e.target.value })
                        }}
                        error={errors.voucherNumber}
                        caption={errors.voucherNumber ? 'Debe ingresar un número de comprobante' : ''}
                        required
                    />
                    <InputDate
                        label='Fecha Compr.'
                        value={new Date()}
                        onChange={(e: BaseSyntheticEvent) => {
                            if (errors.orderDate) {
                                cleanError('orderDate')
                            }
                            setOrderInfo({ ...orderInfo, orderDate: e.target.value })
                        }}
                        maxDate={new Date()}
                        error={errors.orderDate}
                        caption={errors.orderDate ? 'Debe ingresar una fecha de comprobante' : ''}
                    />
                    <InputDate
                        label='Fecha cont.'
                        value={orderInfo.accountDate ? orderInfo.accountDate : orderInfo.orderDate}
                        onChange={(e: BaseSyntheticEvent) => {
                            setOrderInfo({ ...orderInfo, accountDate: e.target.value })
                        }}
                    />
                    <InputDate
                        label='Fecha venc.'
                        value={orderInfo.expirationDate ? orderInfo.expirationDate : orderInfo.orderDate}
                        onChange={(e: BaseSyntheticEvent) => {
                            setOrderInfo({ ...orderInfo, expirationDate: e.target.value })
                        }}
                    />
                </div>
                <div className="flex gap-5 my-3">
                    {creditNotes.includes(orderInfo?.reicipt?.administrativeformtype ?? -1) && <Combobox
                        label="Vincular a "
                        required
                        getItems={getMovementsCombo}
                        params={{
                            supplier: orderInfo.supplier?.medereentity,
                        }}
                        optionLabel="fulldescription"
                        setValue={(value) => {
                            setNoteCredit((prev) => ({ ...prev, noteCredit: value }))
                        }}
                        value={noteCredit?.noteCredit}
                        placeholder="Seleccionar"
                    />}
                    {/*<label className="flex align-items-center gap-2">              //descomentar a futuro
                        <Checkbox
                            checked={orderInfo.payWithCreditCard}
                            onChange={e => setOrderInfo({ ...orderInfo, payWithCreditCard: e.checked ?? false })}
                        />Pago con tarjeta de crédito
                    </label>
                    <label className="flex align-items-center gap-2">
                        <Checkbox
                            checked={orderInfo.operationWithSGR}
                            onChange={e => setOrderInfo((prev) => ({
                                ...prev,
                                operationWithSGR: e.checked ?? false
                            }))}
                        />Operaciones con SGR
                    </label>*/}
                </div>
                <OrderOfProvisionTotals />
                <OrderOfProvisionItems
                    accounting={accounting}
                    items={items}
                    selectedSupplier={selectedSupplier}
                    defaultAccountingAccount={defaultAccountingAccount}
                    deleteVaucherItem={deleteItemGrid}
                    addItemToArray={addItemVaucherToArray}
                    itemTaxesRef={itemTaxesRef}
                    ref={itemsProvisionRef}
                    addItemValidation={() => handleValidation(() => { }, undefined, ['accountDate', 'expirationDate', 'operationWithSGR', 'payWithCreditCard'])}
                    administrativeForm={noteCredit.noteCredit?.administrativeform}
                    supplier={orderInfo.supplier}
                />

                <OrderItemTaxes
                />


                <InputTextCustom
                    label="Nota / Comentario"
                    value={orderInfo.description}
                    onChange={(e) => setOrderInfo((prev) => ({ ...prev, description: e.target.value }))}
                />

                <section className="flex justify-content-end w-full py-3">
                    <Button label="Ver asiento" className="buttonMain2" type="button" onClick={() => { setAccountingModal(true) }} />
                    <Button label="Cancelar" className="buttonMain3" type="button" onClick={() => { clean() }} />
                    <Button label="Confirmar" className="buttonMain" type="submit" loading={isLoading} />
                </section>
            </form>
            <Modal
                visible={infoModal}
                setVisible={setInfoModal}
                header="Atención"
                footerButtonLeftText="Cancelar"
                footerButtonRightText="Continuar"
                onClickRightBtn={() => saveOrder().then(() => setInfoModal(false))}
                onClickLeftBtn={() => setInfoModal(false)}
            >
                <h3>Hay un impuesto o item que no tiene una cuenta contable asociada, ¿desea grabar el comprobante de todas formas?</h3>
            </Modal>

            {accountingModal && <AccountingModal
                visible={accountingModal}
                setVisible={setAccountingModal}
                isInverse={creditNotes.includes(orderInfo?.reicipt?.administrativeformtype ?? -1)}
                data={{
                    supplier: orderInfo.supplier ?? selectedSupplier,
                    items: items,
                    taxes: paidTaxes,
                    date: orderInfo.orderDate ?? new Date(),
                    formType: orderInfo.reicipt,
                    formNumber: orderInfo.pos && orderInfo.voucherNumber ? `${orderInfo.pos} - ${orderInfo.voucherNumber}` : 'N Comprobante',
                }}
            />}
        </div>
    )
}
